import { useMemo } from 'react';

import type {
  UserNetBalanceResponse,
  UserSettingsResponse,
} from '@splitfiesta/core';
import { ApiClientService } from '@splitfiesta/core';

import { useApi } from './useApi';

// Re-export types from ApiClientService for backward compatibility
export type { UserNetBalanceResponse, UserSettingsResponse };

/**
 * Strongly typed API hooks for common operations
 * This provides type-safe wrappers around the base useApi hook
 * Delegates all endpoint definitions to ApiClientService
 */
export function useTypedApi() {
  const api = useApi();

  return useMemo(
    () => ({
      // Keep base methods for backward compatibility
      ...api,

      // Auth endpoints
      auth: {
        login: (
          credentials: Parameters<typeof ApiClientService.auth.login>[1]
        ) => ApiClientService.auth.login(api, credentials),
        register: (
          credentials: Parameters<typeof ApiClientService.auth.register>[1]
        ) => ApiClientService.auth.register(api, credentials),
        refresh: (
          refreshToken: Parameters<typeof ApiClientService.auth.refresh>[1]
        ) => ApiClientService.auth.refresh(api, refreshToken),
        me: () => ApiClientService.auth.me(api),
      },

      // User endpoints
      user: {
        getProfile: () => ApiClientService.user.getProfile(api),
        updateProfile: (
          data: Parameters<typeof ApiClientService.user.updateProfile>[1]
        ) => ApiClientService.user.updateProfile(api, data),
        getSettings: () => ApiClientService.user.getSettings(api),
        updateSettings: (
          data: Parameters<typeof ApiClientService.user.updateSettings>[1]
        ) => ApiClientService.user.updateSettings(api, data),
        updatePassword: (
          data: Parameters<typeof ApiClientService.user.updatePassword>[1]
        ) => ApiClientService.user.updatePassword(api, data),
        acceptTerms: () => ApiClientService.user.acceptTerms(api),
        requestDeletion: (lang?: string) =>
          ApiClientService.user.requestDeletion(api, lang),
        cancelDeletion: () => ApiClientService.user.cancelDeletion(api),
      },
      moderation: {
        reportContent: (
          data: Parameters<typeof ApiClientService.moderation.reportContent>[1]
        ) => ApiClientService.moderation.reportContent(api, data),
      },

      // Group endpoints
      groups: {
        list: (params?: Parameters<typeof ApiClientService.groups.list>[1]) =>
          ApiClientService.groups.list(api, params),
        listWithBalances: (
          params?: Parameters<
            typeof ApiClientService.groups.listWithBalances
          >[1]
        ) => ApiClientService.groups.listWithBalances(api, params),
        getById: (
          id: Parameters<typeof ApiClientService.groups.getById>[1],
          includeBalance?: Parameters<typeof ApiClientService.groups.getById>[2]
        ) => ApiClientService.groups.getById(api, id, includeBalance),
        create: (data: Parameters<typeof ApiClientService.groups.create>[1]) =>
          ApiClientService.groups.create(api, data),
        update: (
          id: Parameters<typeof ApiClientService.groups.update>[1],
          data: Parameters<typeof ApiClientService.groups.update>[2]
        ) => ApiClientService.groups.update(api, id, data),
        delete: (id: Parameters<typeof ApiClientService.groups.delete>[1]) =>
          ApiClientService.groups.delete(api, id),
        removeMember: (
          groupId: Parameters<typeof ApiClientService.groups.removeMember>[1],
          userId: Parameters<typeof ApiClientService.groups.removeMember>[2]
        ) => ApiClientService.groups.removeMember(api, groupId, userId),
        updateMemberRole: (
          groupId: Parameters<
            typeof ApiClientService.groups.updateMemberRole
          >[1],
          memberId: Parameters<
            typeof ApiClientService.groups.updateMemberRole
          >[2],
          role: Parameters<typeof ApiClientService.groups.updateMemberRole>[3]
        ) =>
          ApiClientService.groups.updateMemberRole(
            api,
            groupId,
            memberId,
            role
          ),
        createPlaceholder: (groupId: string, name: string) =>
          ApiClientService.groups.createPlaceholder(api, groupId, name),
        updatePlaceholder: (groupId: string, userId: string, name: string) =>
          ApiClientService.groups.updatePlaceholder(api, groupId, userId, name),
        deletePlaceholder: (groupId: string, userId: string) =>
          ApiClientService.groups.deletePlaceholder(api, groupId, userId),
        listPlaceholderClaims: (groupId: string) =>
          ApiClientService.groups.listPlaceholderClaims(api, groupId),
        confirmPlaceholderClaim: (
          groupId: string,
          userId: string,
          claimantId: string
        ) =>
          ApiClientService.groups.confirmPlaceholderClaim(
            api,
            groupId,
            userId,
            claimantId
          ),
        requestPlaceholderClaim: (groupId: string, userId: string) =>
          ApiClientService.groups.requestPlaceholderClaim(api, groupId, userId),
        getSimplifiedDebts: (
          groupId: Parameters<
            typeof ApiClientService.groups.getSimplifiedDebts
          >[1],
          filterByUser: Parameters<
            typeof ApiClientService.groups.getSimplifiedDebts
          >[2] = false
        ) =>
          ApiClientService.groups.getSimplifiedDebts(
            api,
            groupId,
            filterByUser
          ),
      },

      // Expense endpoints
      expenses: {
        list: (
          groupId: Parameters<typeof ApiClientService.expenses.list>[1],
          params?: Parameters<typeof ApiClientService.expenses.list>[2]
        ) => ApiClientService.expenses.list(api, groupId, params),
        getById: (
          id: Parameters<typeof ApiClientService.expenses.getById>[1]
        ) => ApiClientService.expenses.getById(api, id),
        create: (
          data: Parameters<typeof ApiClientService.expenses.create>[1]
        ) => ApiClientService.expenses.create(api, data),
        update: (
          id: Parameters<typeof ApiClientService.expenses.update>[1],
          data: Parameters<typeof ApiClientService.expenses.update>[2]
        ) => ApiClientService.expenses.update(api, id, data),
        delete: (id: Parameters<typeof ApiClientService.expenses.delete>[1]) =>
          ApiClientService.expenses.delete(api, id),
        claimLineItems: (
          expenseId: Parameters<
            typeof ApiClientService.expenses.claimLineItems
          >[1],
          body: Parameters<typeof ApiClientService.expenses.claimLineItems>[2]
        ) => ApiClientService.expenses.claimLineItems(api, expenseId, body),
        addComment: (
          expenseId: Parameters<typeof ApiClientService.expenses.addComment>[1],
          data: Parameters<typeof ApiClientService.expenses.addComment>[2]
        ) => ApiClientService.expenses.addComment(api, expenseId, data),
        updateComment: (
          commentId: Parameters<
            typeof ApiClientService.expenses.updateComment
          >[1],
          data: Parameters<typeof ApiClientService.expenses.updateComment>[2]
        ) => ApiClientService.expenses.updateComment(api, commentId, data),
        deleteComment: (
          commentId: Parameters<
            typeof ApiClientService.expenses.deleteComment
          >[1],
          expenseId: Parameters<
            typeof ApiClientService.expenses.deleteComment
          >[2]
        ) => ApiClientService.expenses.deleteComment(api, commentId, expenseId),
        addReaction: (
          expenseId: Parameters<
            typeof ApiClientService.expenses.addReaction
          >[1],
          reaction: Parameters<typeof ApiClientService.expenses.addReaction>[2]
        ) => ApiClientService.expenses.addReaction(api, expenseId, reaction),
        removeReaction: (
          expenseId: Parameters<
            typeof ApiClientService.expenses.removeReaction
          >[1],
          reaction: Parameters<
            typeof ApiClientService.expenses.removeReaction
          >[2]
        ) => ApiClientService.expenses.removeReaction(api, expenseId, reaction),
        predictCategory: (
          params: Parameters<
            typeof ApiClientService.expenses.predictCategory
          >[1]
        ) => ApiClientService.expenses.predictCategory(api, params),
      },

      // Exchange rate endpoints
      exchangeRates: {
        get: (
          params: Parameters<typeof ApiClientService.exchangeRates.get>[1]
        ) => ApiClientService.exchangeRates.get(api, params),
      },

      // Feature flag endpoints
      featureFlags: {
        get: () => ApiClientService.featureFlags.get(api),
      },

      pro: {
        addGroup: (body: Parameters<typeof ApiClientService.pro.addGroup>[1]) =>
          ApiClientService.pro.addGroup(api, body),
        removeGroup: (groupId: string) =>
          ApiClientService.pro.removeGroup(api, groupId),
        listGroups: () => ApiClientService.pro.listGroups(api),
      },

      recurringRules: {
        list: (groupId: string) =>
          ApiClientService.recurringRules.list(api, groupId),
        updateStatus: (
          groupId: string,
          ruleId: string,
          body: Parameters<
            typeof ApiClientService.recurringRules.updateStatus
          >[3]
        ) =>
          ApiClientService.recurringRules.updateStatus(
            api,
            groupId,
            ruleId,
            body
          ),
        stop: (groupId: string, ruleId: string) =>
          ApiClientService.recurringRules.stop(api, groupId, ruleId),
      },

      // CSV import endpoints
      imports: {
        createSplitwise: (
          body: Parameters<typeof ApiClientService.imports.createSplitwise>[1]
        ) => ApiClientService.imports.createSplitwise(api, body),
        getJob: (
          jobId: Parameters<typeof ApiClientService.imports.getJob>[1]
        ) => ApiClientService.imports.getJob(api, jobId),
        resumeJob: (
          jobId: Parameters<typeof ApiClientService.imports.resumeJob>[1]
        ) => ApiClientService.imports.resumeJob(api, jobId),
        resolveLabels: (
          body: Parameters<typeof ApiClientService.imports.resolveLabels>[1]
        ) => ApiClientService.imports.resolveLabels(api, body),
      },

      // Balance endpoints
      balances: {
        getForGroup: (
          groupId: Parameters<typeof ApiClientService.balances.getForGroup>[1]
        ) => ApiClientService.balances.getForGroup(api, groupId),
        getForUser: () => ApiClientService.balances.getForUser(api),
        getUserNet: () => ApiClientService.balances.getUserNet(api),
      },

      // Settlement endpoints
      settlements: {
        list: (
          groupId: Parameters<typeof ApiClientService.settlements.list>[1],
          params?: Parameters<typeof ApiClientService.settlements.list>[2]
        ) => ApiClientService.settlements.list(api, groupId, params),
        create: (
          data: Parameters<typeof ApiClientService.settlements.create>[1]
        ) => ApiClientService.settlements.create(api, data),
        update: (
          id: Parameters<typeof ApiClientService.settlements.update>[1],
          data: Parameters<typeof ApiClientService.settlements.update>[2]
        ) => ApiClientService.settlements.update(api, id, data),
        summary: (
          groupId: Parameters<typeof ApiClientService.settlements.summary>[1]
        ) => ApiClientService.settlements.summary(api, groupId),
      },

      // Activity endpoints
      activity: {
        getFeed: (
          params?: Parameters<typeof ApiClientService.activity.getFeed>[1]
        ) => ApiClientService.activity.getFeed(api, params),
      },

      // Receipt drafts and scans
      receipts: {
        createDraft: (
          groupId: string,
          body: Parameters<typeof ApiClientService.receipts.createDraft>[2]
        ) => ApiClientService.receipts.createDraft(api, groupId, body),
        scanDraft: (
          groupId: string,
          draftId: string,
          body: Parameters<typeof ApiClientService.receipts.scanDraft>[3]
        ) => ApiClientService.receipts.scanDraft(api, groupId, draftId, body),
      },

      // Upload endpoints
      upload: {
        getPresignedUrl: (
          data: Parameters<typeof ApiClientService.upload.getPresignedUrl>[1]
        ) => ApiClientService.upload.getPresignedUrl(api, data),
        confirm: (
          data: Parameters<typeof ApiClientService.upload.confirm>[1]
        ) => ApiClientService.upload.confirm(api, data),
      },

      // Admin endpoints
      admin: {
        startShadow: (
          body: Parameters<typeof ApiClientService.admin.startShadow>[1]
        ) => ApiClientService.admin.startShadow(api, body),
        endShadow: (
          body: Parameters<typeof ApiClientService.admin.endShadow>[1]
        ) => ApiClientService.admin.endShadow(api, body),
        searchUsers: (
          query: Parameters<typeof ApiClientService.admin.searchUsers>[1]
        ) => ApiClientService.admin.searchUsers(api, query),
      },

      // Invitation endpoints
      invitations: {
        create: (
          groupId: Parameters<typeof ApiClientService.invitations.create>[1]
        ) => ApiClientService.invitations.create(api, groupId),
        validate: (
          token: Parameters<typeof ApiClientService.invitations.validate>[1]
        ) => ApiClientService.invitations.validate(api, token),
        accept: (
          token: Parameters<typeof ApiClientService.invitations.accept>[1],
          claimPlaceholderId?: Parameters<
            typeof ApiClientService.invitations.accept
          >[2]
        ) =>
          ApiClientService.invitations.accept(api, token, claimPlaceholderId),
        delete: (
          invitationId: Parameters<
            typeof ApiClientService.invitations.delete
          >[1]
        ) => ApiClientService.invitations.delete(api, invitationId),
      },
    }),
    [api]
  );
}

// Export types for use in components
export type TypedApi = ReturnType<typeof useTypedApi>;
export type AuthApi = TypedApi['auth'];
export type UserApi = TypedApi['user'];
export type GroupsApi = TypedApi['groups'];
export type ExpensesApi = TypedApi['expenses'];
export type BalancesApi = TypedApi['balances'];
